// Saving one phone's or email's consent from the office (customers.setConsent). While the company mirrors
// ServiceTitan the contact's consent is ServiceTitan's and setConsent is refused; a phone's texts turned
// off are the customer's word all the same, so they're kept here (customers.recordOptOut), every text to
// the number stops, and the office is told to set it in ServiceTitan too (QA-615). Anything else the
// mirror refuses (texts back on, an email, marketing) is refused in its words.
import type { Built } from '../shared/EditSheet.tsx';
import type { ContactMethod, Customer } from '../types.ts';
import { consentInput, type ConsentForm } from './customer-edit-model.ts';

/** What the sheet says once a texts-off is kept here while mirroring. */
export const OPT_OUT_KEPT =
  'Texts to this number are off here now. ServiceTitan still has them on until cutover: mark the number do-not-text there too.';

/** This form turns a phone's texts off: consent unticked, or do-not-contact ticked. */
export const turnsTextsOff = (m: ContactMethod, f: ConsentForm): boolean =>
  m.kind === 'phone' && ((m.consent && !f.consent) || (f.doNotContact && !m.doNotContact));

const mirrored = (e: unknown): boolean =>
  typeof e === 'object' &&
  e !== null &&
  (e as { detail?: { reason?: string } }).detail?.reason === 'mirrored';

/** The sheet's save: setConsent, or, refused as mirrored for a texts-off, customers.recordOptOut. */
export async function saveConsent(
  w: {
    run: <T>(
      action: string,
      built: Built,
      step?: string,
      orElse?: (e: unknown) => { action: string; input: unknown } | null,
    ) => Promise<T | null>;
  },
  m: ContactMethod,
  f: ConsentForm,
): Promise<{ saved: Customer | null; kept: string | null }> {
  let kept = false;
  const saved = await w.run<Customer>('customers.setConsent', consentInput(m, f), undefined, (e) => {
    if (!turnsTextsOff(m, f) || !mirrored(e)) return null;
    kept = true;
    return { action: 'customers.recordOptOut', input: { contactMethodId: m.id } };
  });
  return { saved, kept: saved && kept ? OPT_OUT_KEPT : null };
}
